"use client"

import { useEffect, useRef, useState, useCallback } from "react"
import { Box, Typography, CircularProgress } from "@mui/material"
import VideoCard from "./VideoCard"
import axios from "axios"
import AnimatedLoader from "reuseableTable/AnimatedLoader"
import NoAvailableData from "reuseableTable/NoAvailableData"

const VideosForMentoring = () => {
  const [videos, setVideos] = useState([])
  const [loadingVideos, setLoadingVideos] = useState(false)
  const [playingVideoId, setPlayingVideoId] = useState(null)
  const playerRefs = useRef({})
  const observerRef = useRef(null)

  const [page, setPage] = useState(0)
  const [pageSize] = useState(10)
  const [totalPages, setTotalPages] = useState(0)
  const [hasMore, setHasMore] = useState(true)

  const RequestId = (a, b) => `${a}-${b}`
  const rendomRequestId = Math.floor(Math.random() * 1000)

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.hidden && playingVideoId) {
        const videoElement = playerRefs.current[playingVideoId]?.getInternalPlayer()
        if (videoElement && document.pictureInPictureEnabled) {
          try {
            if (document.pictureInPictureElement) {
              await document.exitPictureInPicture()
            }
            await videoElement.requestPictureInPicture()
          } catch (error) {
            console.error("Error enabling PiP:", error)
          }
        }
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [playingVideoId])
  useEffect(() => {
    return () => {
      Object.keys(playerRefs.current).forEach((id) => {
        if (playerRefs.current[id]) {
          playerRefs.current[id] = null
        }
      })
    }
  }, [])

  const handlePause = () => {
    setPlayingVideoId(null)
  }

  const handlePlay = async (videoId) => {
    if (document.pictureInPictureElement) {
      try {
        await document.exitPictureInPicture()
      } catch (error) {
        console.error("Error exiting PiP:", error)
      }
    }
    Object.keys(playerRefs.current).forEach((id) => {
      if (id !== videoId && playerRefs.current[id]) {
        const player = playerRefs.current[id]
        if (player.getInternalPlayer && player.getInternalPlayer() && !player.getInternalPlayer().paused) {
          player.getInternalPlayer().pause()
        }
      }
    })

    setPlayingVideoId(videoId)

    try {
      // Uncomment this when you have the actual API
      await axios.get(`/v1/alumni/video/update/views?videoId=${videoId}`, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem(
            "serviceToken"
          )}`,
          "Content-Type": "application/json",
          requestId: RequestId(
            Math.floor(1000 + Math.random() * 9999),
            Math.floor(1000 + Math.random() * 9999 + rendomRequestId)
          ),
        },
      });
    } catch (error) {
      console.error("Error updating view count", error)
    }
  }

  const lastVideoRef = useCallback(
    (node) => {
      if (loadingVideos) return
      if (observerRef.current) observerRef.current.disconnect()

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1)
        }
      })

      if (node) observerRef.current.observe(node)
    },
    [loadingVideos, hasMore],
  )

  const getVideos = async (pageNumber = page) => {
    if (loadingVideos) return
    setLoadingVideos(true)
    try {
      const response = await axios.get(
        `/v1/alumni/video/filter-list?pageSize=${pageSize}&pageNumber=${pageNumber}&categoryId=&subCategoryId=`,
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("serviceToken")}`,
            "Content-Type": "application/json",
            requestId: RequestId(
              Math.floor(1000 + Math.random() * 9999),
              Math.floor(1000 + Math.random() * 9999 + rendomRequestId)
            ),
          },
        }
      );

      const newVideos = response.data.data.content || [];
      setTotalPages(response.data.data.totalPages || 0);
      await new Promise((resolve) => setTimeout(resolve, 1000))
      if (pageNumber >= 4) {
        setHasMore(false)
      }

      setVideos((prev) => [...prev, ...newVideos])
      setLoadingVideos(false)
    } catch (error) {
      setLoadingVideos(false)
      console.error("Error fetching videos:", error)
    }
  }

  const handleLikeVideo = async (videoId) => {
    try {
      setVideos((prevVideos) =>
        prevVideos.map((video) => {
          if (video.id === videoId) {
            return {
              ...video,
              likes: video.likes + 1,
              liked: true,
            };
          }
          return video;
        })
      );

      await axios.get(`v1/alumni/video/update/likes?videoId=${videoId}`, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("serviceToken")}`,
          "Content-Type": "application/json",
          requestId: RequestId(
            Math.floor(1000 + Math.random() * 9999),
            Math.floor(1000 + Math.random() * 9999 + rendomRequestId)
          ),
        },
      });
    } catch (error) {
      console.error("Error updating like count:", error);
      setVideos((prevVideos) =>
        prevVideos.map((video) => {
          if (video.id === videoId) {
            return {
              ...video,
              liked: video.liked,
            };
          }
          return video;
        })
      );
    }
  };

  useEffect(() => {
    getVideos(page)
  }, [page])



  return (
    <>

      {/* No videos message */}
      {!loadingVideos && videos.length === 0 && (
        <>
          <NoAvailableData text="No videos yet — stay tuned!" />
        </>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          maxWidth: "400px",
          mx: "auto",
          p: 2,
          minHeight: "100vh",
        }}
      >
        {/* Videos list */}
        {videos.map((video, index) => {
          if (index === videos.length - 1) {
            return (
              <Box
                ref={lastVideoRef}
                key={video.id}
                sx={{ display: "flex", justifyContent: "center" }}
              >
                <VideoCard
                  title={video.videoCategory?.name}
                  maintitle={video.title}
                  videoPath={video.videoPath}
                  subTitle={
                    video.alumniMemberMini !== null
                      ? video?.alumniMemberMini?.name
                      : video.createdByName
                  }
                  date={video?.approvalByDate}
                  view={video?.views || 0}
                  likes={video?.likes || 0}
                  liked={video?.liked}
                  videoId={video.id}
                  onPlay={handlePlay}
                  onPause={handlePause}
                  handleLikeVideo={handleLikeVideo}
                  alumniData={video?.alumniMemberMini}
                  isPlaying={playingVideoId === video.id}
                  ref={(el) => (playerRefs.current[video.id] = el)}
                />
              </Box>
            )
          }

          return (
            <Box
              key={video.id}
              sx={{ width: "100%", display: "flex", justifyContent: "center" }}
            >
              <VideoCard
                title={video.videoCategory?.name}
                maintitle={video.title}
                videoPath={video.videoPath}
                subTitle={
                  video.alumniMemberMini !== null
                    ? video?.alumniMemberMini?.name
                    : video.createdByName
                }
                date={video?.createdAt}
                view={video?.views || 0}
                likes={video?.likes || 0}
                liked={video?.liked}
                videoId={video.id}
                onPlay={handlePlay}
                onPause={handlePause}
                handleLikeVideo={handleLikeVideo}
                alumniData={video?.alumniMemberMini}
                isPlaying={playingVideoId === video.id}
                ref={(el) => (playerRefs.current[video.id] = el)}
              />
            </Box>
          )
        })}

        {/* Loader */}
        {loadingVideos && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <AnimatedLoader />
          </Box>
        )}

        {/* End message */}
        {!hasMore && videos.length > 0 && (
          <Typography sx={{ py: 3, color: "text.secondary", textAlign: "center" }}>
            No more videos to load
          </Typography>
        )}
      </Box>

    </>
  )
}

export default VideosForMentoring
